import axios from 'axios';

// Configuración base de la API
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
//console.log('API_BASE_URL', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Función para obtener el token de autenticación
let getAuthToken = null;

// Función para configurar el token getter
export const setAuthTokenGetter = (tokenGetter) => {
  getAuthToken = tokenGetter;
};

// Interceptor para agregar token de autenticación
api.interceptors.request.use(
  async (config) => {
    if (getAuthToken) {
      try {
        const token = await getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.warn('No se pudo obtener el token de autenticación:', error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejo de errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// Servicios para autos
export const carService = {
  // Obtener todos los autos con filtros
  getAllCars: async (filters = {}) => {
    try {
      const params = new URLSearchParams();

      if (filters.search) params.append('search', filters.search);
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);
      if (filters.min_kms) params.append('min_kms', filters.min_kms);
      if (filters.max_kms) params.append('max_kms', filters.max_kms);
      if (filters.sort_by) params.append('sort_by', filters.sort_by);
      if (filters.sort_order) params.append('sort_order', filters.sort_order);
      if (filters.query) params.append('query', filters.query);
      if (filters.min_year) params.append('min_year', filters.min_year);
      if (filters.max_year) params.append('max_year', filters.max_year);
      if (filters.page) params.append('page', filters.page);
      if (filters.per_page) params.append('per_page', filters.per_page);

      const response = await api.get(`/cars?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener los autos');
    }
  },

  // Obtener auto por ID
  getCarById: async (id) => {
    try {
      const response = await api.get(`/cars/${id}`);
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener el auto');
    }
  },

  // Obtener historial de precios
  getPriceHistory: async (id) => {
    try {
      const response = await api.get(`/cars/${id}/price-history`);
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener el historial de precios');
    }
  },

  // Crear nuevo auto
  createCar: async (carData) => {
    try {
      const response = await api.post('/cars', carData);
      return response.data;
    } catch (error) {
      throw new Error('Error al crear el auto');
    }
  },

  // Actualizar auto
  updateCar: async (id, carData) => {
    try {
      const response = await api.put(`/cars/${id}`, carData);
      return response.data;
    } catch (error) {
      throw new Error('Error al actualizar el auto');
    }
  },

  // Eliminar auto
  deleteCar: async (id) => {
    try {
      await api.delete(`/cars/${id}`);
      return true;
    } catch (error) {
      throw new Error('Error al eliminar el auto');
    }
  },

  // Verificar salud de la API
  checkHealth: async () => {
    try {
      const response = await api.get('/health');
      return response.data;
    } catch (error) {
      throw new Error('Error al verificar la conexión');
    }
  },

  // Obtener queries disponibles
  getQueries: async () => {
    try {
      const response = await api.get('/queries');
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener las queries');
    }
  },

  // Obtener años disponibles para una categoría
  getYears: async (query) => {
    try {
      const params = query ? `?query=${query}` : '';
      const response = await api.get(`/years${params}`);
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener los años');
    }
  },

  // Obtener vehículos despublicados
  getUnpublishedCars: async (query) => {
    try {
      const params = query ? `?query=${query}` : '';
      const response = await api.get(`/cars/unpublished${params}`);
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener los vehículos despublicados');
    }
  },

  // Obtener precio máximo sugerido para un vehículo
  getMaxPrice: async ({ query, version, year, km_category, id }) => {
    try {
      const params = new URLSearchParams();
      if (id) params.append('id', id);
      if (query) params.append('query', query);
      if (version) params.append('version', version);
      if (year) params.append('year', year);
      if (km_category) params.append('km_category', km_category);
      const response = await api.get(`/max-price?${params.toString()}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};

// Servicios para prospección
export const prospectionService = {
  // Obtener prospecciones con filtros
  getAllProspects: async (filters = {}) => {
    try {
      const params = {};

      if (filters.page) params.page = filters.page;
      if (filters.per_page) params.per_page = filters.per_page;
      if (filters.lead_type) params.lead_type = filters.lead_type;
      if (filters.status) params.status = filters.status;
      if (filters.username) params.username = filters.username;

      const response = await api.get('/prospection', { params });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || 'Error al obtener las prospecciones';
      throw new Error(message);
    }
  },

  // Obtener prospección por ID
  getProspectById: async (id) => {
    try {
      const response = await api.get(`/prospection/${id}`);
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener la prospección');
    }
  },

  // Crear nueva prospección
  createProspect: async (prospectData) => {
    try {
      const response = await api.post('/prospection', prospectData);
      return response.data;
    } catch (error) {
      throw new Error('Error al crear la prospección');
    }
  },

  // Actualizar prospección
  updateProspect: async (id, prospectData) => {
    try {
      const response = await api.put(`/prospection/${id}`, prospectData);
      return response.data;
    } catch (error) {
      throw new Error('Error al actualizar la prospección');
    }
  },

  // Eliminar prospección
  deleteProspect: async (id) => {
    try {
      await api.delete(`/prospection/${id}`);
      return true;
    } catch (error) {
      throw new Error('Error al eliminar la prospección');
    }
  },

  // Obtener estadísticas de prospección
  getStats: async () => {
    try {
      const response = await api.get('/prospection/stats');
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || 'Error al obtener las estadísticas';
      throw new Error(message);
    }
  }
};

// Servicios para motos
export const motoService = {
  getAllMotos: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);
      if (filters.min_kms) params.append('min_kms', filters.min_kms);
      if (filters.max_kms) params.append('max_kms', filters.max_kms);
      if (filters.sort_by) params.append('sort_by', filters.sort_by);
      if (filters.sort_order) params.append('sort_order', filters.sort_order);
      if (filters.query) params.append('query', filters.query);
      if (filters.min_year) params.append('min_year', filters.min_year);
      if (filters.max_year) params.append('max_year', filters.max_year);
      if (filters.page) params.append('page', filters.page);
      if (filters.per_page) params.append('per_page', filters.per_page);
      const response = await api.get(`/motos?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener las motos');
    }
  },
  getQueries: async () => {
    const response = await api.get('/motos/queries');
    return response.data;
  },
  getYears: async (query) => {
    const params = query ? `?query=${query}` : '';
    const response = await api.get(`/motos/years${params}`);
    return response.data;
  },
  getUnpublished: async (query) => {
    const params = query ? `?query=${query}` : '';
    const response = await api.get(`/motos/unpublished${params}`);
    return response.data;
  },
  getStats: async (query) => {
    const params = query ? `?query=${query}` : '';
    const response = await api.get(`/motos/stats${params}`);
    return response.data;
  }
};

// Servicios para suscripciones
export const subscriptionService = {
  subscribe: async (vehicleId, vehicleType = 'car') => {
    const response = await api.post('/subscriptions', { vehicle_id: vehicleId, vehicle_type: vehicleType });
    return response.data;
  },
  unsubscribe: async (vehicleId) => {
    const response = await api.delete(`/subscriptions/${vehicleId}`);
    return response.data;
  },
  getAll: async (page = 1, perPage = 20) => {
    const response = await api.get(`/subscriptions?page=${page}&per_page=${perPage}`);
    return response.data;
  },
  check: async (vehicleId) => {
    const response = await api.get(`/subscriptions/check/${vehicleId}`);
    return response.data;
  }
};

// Servicios para notificaciones
export const notificationService = {
  getAll: async (page = 1, perPage = 20) => {
    const response = await api.get(`/notifications?page=${page}&per_page=${perPage}`);
    return response.data;
  },
  getUnreadCount: async () => {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  },
  markRead: async (notificationIds) => {
    const response = await api.put('/notifications/mark-read', { notification_ids: notificationIds });
    return response.data;
  }
};

export default api;
